import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle, PhoneCall, Save } from "lucide-react";
import { format } from "date-fns";

const QAWorkdeskPage = () => {
  const { authAxios, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingCalls, setPendingCalls] = useState([]);
  const [matrices, setMatrices] = useState([]);
  
  const [selectedCall, setSelectedCall] = useState(null);
  const [selectedMatrix, setSelectedMatrix] = useState("");
  const [score, setScore] = useState("");
  const [comments, setComments] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const [callsRes, matricesRes] = await Promise.all([
        authAxios.get("/qa/pending?limit=20"),
        authAxios.get("/matrices")
      ]);
      setPendingCalls(callsRes.data);
      setMatrices(matricesRes.data);
    } catch (error) {
      console.error("Error fetching QA data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEvaluate = async () => {
    if (!selectedCall || !selectedMatrix || !score) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    const matrix = matrices.find(m => m.id === selectedMatrix);

    try {
      await authAxios.post(`/qa/evaluate/${selectedCall.id}`, {
        matrix_id: selectedMatrix,
        matrix_name: matrix?.name || "Matriz Manual",
        evaluator_id: user.id || "admin",
        evaluator_name: user.name || "Admin",
        score: parseFloat(score),
        comments: comments
      });

      toast.success("Llamada evaluada correctamente");
      setSelectedCall(null);
      setSelectedMatrix("");
      setScore("");
      setComments("");
      fetchData(); // Refresh list
    } catch (error) {
      console.error("Error saving evaluation:", error);
      toast.error("Error al guardar evaluación");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Workdesk QA</h1>
        <p className="text-slate-500 mt-1">Evalúa llamadas manualmente y gestiona tu trabajo de calidad.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Calls List */}
        <div className="lg:col-span-1 space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-sky-600" />
            Pendientes de Evaluar ({pendingCalls.length})
          </h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
            {pendingCalls.length === 0 ? (
              <p className="text-sm text-slate-500 p-4 border rounded bg-slate-50">No hay llamadas pendientes de evaluar.</p>
            ) : (
              pendingCalls.map(call => (
                <Card 
                  key={call.id} 
                  className={`cursor-pointer transition-all hover:border-sky-500 ${selectedCall?.id === call.id ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500' : ''}`}
                  onClick={() => setSelectedCall(call)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-slate-900">{call.agent || "Agente Desconocido"}</span>
                      <span className="text-xs text-slate-500">
                        {call.call_date ? format(new Date(call.call_date), "dd MMM HH:mm") : "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <PhoneCall className="w-3 h-3" />
                      <span>{call.phone_number || "Sin número"}</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-500 line-clamp-2">
                      {call.summary || "Sin resumen disponible"}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Evaluation Form */}
        <div className="lg:col-span-2">
          {selectedCall ? (
            <Card>
              <CardHeader>
                <CardTitle>Evaluación de Llamada</CardTitle>
                <div className="text-sm text-slate-500">
                  Agente: {selectedCall.agent} | ID: {selectedCall.id}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-1 block">Matriz de Calidad *</label>
                  <select 
                    className="w-full border-slate-200 rounded-md p-2 border"
                    value={selectedMatrix}
                    onChange={(e) => setSelectedMatrix(e.target.value)}
                  >
                    <option value="">Selecciona una matriz...</option>
                    {matrices.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Calificación Final (0-100) *</label>
                  <Input 
                    type="number" 
                    min="0" max="100" 
                    placeholder="Ej. 95" 
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">Calificación global asignada a la atención del agente.</p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Comentarios del Supervisor</label>
                  <Textarea 
                    placeholder="Escribe aquí las oportunidades de mejora o felicitaciones..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleEvaluate} className="bg-sky-600 hover:bg-sky-700">
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Evaluación
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="h-full flex items-center justify-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50 p-8 text-center">
              <div>
                <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900">Selecciona una llamada</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                  Haz clic en una de las llamadas de la lista de pendientes para comenzar a evaluar.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QAWorkdeskPage;
